import React, { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import AppPaths from '../../../AppPaths';
import ImageGallery from '../../../components/ImageGallery/ImageGallery';
import * as events from '../../../lib/analytics/events';
import { cartActions as storeCartActions } from '../../../reducers/storeCart';
import AddToBagButton from '../components/AddToBagButton';
import ProductTitle from '../components/ProductTitle';
import styles from '../ProductDetails.module.scss';
import { getFiGrowthBook } from '../../../lib/growthbook';
import { useLocale } from '../../../contexts/LocaleContext';

interface ChargingBaseDetailsProps {
  productId: string;
  productName: string;
  priceInCents: number;
  sku: string;
}

export default function ChargingBaseDetails({ productId, productName, priceInCents, sku }: ChargingBaseDetailsProps) {
  const { t } = useTranslation();
  useEffect(() => {
    events.chargingBaseViewed({ priceInCents, productName, sku });
  }, [priceInCents, productName, sku]);

  const dispatch = useDispatch();
  const history = useHistory();
  const { currency } = useLocale();

  const onAddToBag = useCallback(() => {
    dispatch(storeCartActions.addSingleLineCartItem({ sku, currency }));

    events.chargingBaseAddedToBag({
      priceInCents,
      productName,
      sku,
    });

    history.push(AppPaths.Bag);

    return true;
  }, [dispatch, history, priceInCents, productName, sku, currency]);

  const isDiscontinued = sku === 'F1-BUA';
  const miniPDPEnabled = getFiGrowthBook().getFeatureValue<boolean>('ecom-mini-pdp', false);

  return (
    <>
      <div className={styles.productContainer}>
        <ImageGallery
          media={[
            {
              type: 'image',
              url: `/product_images/${productId}.jpg`,
              retinaWidth: 1080,
              retinaHeight: 720,
            },
          ]}
        />
        <div className={styles.productDetails}>
          <div className={styles.productDetailsHeader}>
            <div className={styles.productDetailsTitle}>
              <ProductTitle productName={productName} price={{ priceInCents }} />
            </div>
            <p className={styles.description}>
              {t('this_is_an_extra_fi_base_charging_adapter_and_cable_included')}
              <br />
              <br />
              {t(
                'use_it_to_extend_the_coverage_in_your_home_cover_your_pup_at_the_office_your_beach_house_or_for_when_rufus_spends_the_weekend_at_his_moms_place',
              )}
              {miniPDPEnabled && (
                <>
                  <br />
                  <br />
                  {t(
                    'nearby_fi_mini_devices_can_connect_to_a_fi_base_via_bluetooth_to_reduce_their_power_consumption_and_extend_their_battery_life_fi_mini_devices_cannot_charge_on_a_fi_base',
                  )}
                </>
              )}
            </p>
          </div>

          <div className={styles.productAction}>
            {isDiscontinued ? (
              <AddToBagButton onAddToBag={onAddToBag} disabled={true} ctaText={t('discontinued')} />
            ) : (
              <AddToBagButton onAddToBag={onAddToBag} />
            )}
          </div>
        </div>
      </div>
    </>
  );
}
